import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Redirects subcontractor-only users from a building sub-route that they can't
 * see (the building dashboard and Building Data — members are Projects-view
 * only; the project dashboard is theirs) to the building's Files page (whose Folders tab is
 * their working surface).
 *
 * Non-subcontractor users render `children` unchanged. This lets us keep the
 * existing routes working for all current roles while blocking sub-only users
 * from accidentally landing on building-wide surfaces.
 *
 * The query string is carried along so a `?project=` scope deep link (e.g. a
 * project clicked on My Buildings) survives the redirect.
 */
export function SubcontractorRedirect({ children }: { children: ReactNode }) {
  const { isSubcontractorOnly } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  if (isSubcontractorOnly && id) {
    return <Navigate to={`/buildings/${id}/documents${location.search}`} replace />;
  }

  return <>{children}</>;
}
